/**
 * Serviço de Integração com o Sistema Argos (SRE / Helpdesk)
 */

const ARGOS_API_URL = 'https://argos-gestor-sistemas--argos-gestor-sistemas.us-east4.hosted.app';
const ARGOS_SISTEMA_ID = 'mago';
const ARGOS_API_KEY = process.env.REACT_APP_ARGOS_API_KEY;

/**
 * Converte um arquivo em uma representação Base64 (Data URL)
 * @param {File} file - Arquivo vindo de um input ou drag & drop
 * @returns {Promise<string>} - Promise contendo o Base64 Data URL
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Busca todos os chamados abertos para o sistema Mago no Argos
 * @returns {Promise<Array>} - Lista de chamados
 */
export const fetchTickets = async () => {
  const url = `${ARGOS_API_URL}/api/tickets?sistema_id=${ARGOS_SISTEMA_ID}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'x-api-key': ARGOS_API_KEY,
        'x-argos-api-key': ARGOS_API_KEY
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Erro HTTP ${response.status} ao carregar chamados.`);
    }

    const data = await response.json();
    return data.tickets || [];
  } catch (error) {
    console.error('[Argos Service] Erro ao buscar chamados:', error);
    throw error;
  }
};

/**
 * Envia um novo chamado para a central do Argos
 * @param {Object} params
 * @param {string} params.titulo - Título descritivo do incidente
 * @param {string} params.descricao - Detalhes do erro relatado
 * @param {string} params.prioridade - 'baixa' | 'media' | 'alta'
 * @param {string} [params.origem] - Tela ou fluxo de origem do chamado
 * @param {Array<Object>} [params.anexos] - Lista de arquivos serializados
 * @param {Object} [params.metadata] - Informações adicionais de ambiente
 * @returns {Promise<Object>} - Resultado da requisição
 */
export const sendTicket = async ({
  titulo,
  descricao,
  prioridade = 'media',
  origem = 'Mago Front-end',
  anexos = [],
  metadata = {}
}) => {
  const url = `${ARGOS_API_URL}/api/tickets/receive`;

  // Montagem do payload_bruto_stack_trace contendo as informações organizadas
  const ambiente = metadata.ambiente || 'desenvolvimento';
  const versao = metadata.versao || '1.0.0';
  const usuario = metadata.usuario_afetado || 'sistema@mago.com';
  const stack = metadata.stack_trace ? `\n\nStack Trace:\n${metadata.stack_trace}` : '';
  
  const payload_bruto_stack_trace = `Título: ${titulo}
Ambiente: ${ambiente}
Versão: ${versao}
Usuário Afetado: ${usuario}
Origem: ${origem}

Descrição do Incidente:
${descricao}${stack}`;

  // Formatação dos anexos para o contrato do Argos
  const formatadosAnexos = anexos.map(anexo => ({
    url: anexo.base64, // O Argos espera data_url ou url no campo "url"
    nome: anexo.filename,
    tipo: anexo.mimeType,
    tamanho_bytes: anexo.size
  }));

  const payload = {
    sistema_id: ARGOS_SISTEMA_ID,
    criticidade: prioridade,
    origem,
    payload_bruto_stack_trace,
    anexos: formatadosAnexos
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-api-key': ARGOS_API_KEY,
        'x-argos-api-key': ARGOS_API_KEY
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Erro HTTP ${response.status} ao enviar chamado.`);
    }

    return await response.json();
  } catch (error) {
    console.error('[Argos Service] Erro ao enviar chamado:', error);
    throw error;
  }
};
